import { LayerSwitcher, MapGL } from '@cibo/mapgl'
import { Box, Divider, Paper, Stack, Typography } from '@mui/material'
import { InventoryMapLayers } from 'features/Inventories/components/InventoryMapLayers'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useEffect, useMemo, useState } from 'react'

import { LoadingButton } from '@mui/lab'
import { FeatureCollection, featureCollection } from '@turf/turf'
import * as d3 from 'd3'
import { useBoundaryFeature, useInventory, useSetBoundaryOfftakes } from 'features/Inventories'
import { BoundariesListItem } from 'features/Inventories/components/BoundariesList/BoundariesListItem'
import { Feature, Geometry } from 'geojson'
import { NavigationControl } from 'react-map-gl/dist/es5'
import { ItemLocator } from '../../../consts/Theme'
import { BoundaryAreaLayer } from './BoundaryAreaLayer'
import { HistoricalOfftakeTable } from './HistoricalOfftakeTable'

const LATEST_OFFTAKE_YEAR = 2023

export const HistoricalOfftake = () => {
  const { t } = useTranslation('impact/Inventories/HistoricalOfftake')
  const { inventoryId } = useParams()
  const [selectedBoundaryIndex, setSelectedBoundaryIndex] = useState(0)

  const inventory = useInventory(inventoryId)

  const boundary = inventory.data?.item.boundaries[selectedBoundaryIndex]

  const boundaryFeature = useBoundaryFeature(boundary)

  const setBoundaryOfftakes = useSetBoundaryOfftakes()

  const [updatedOfftakes, setUpdatedOfftakes] = useState()
  useEffect(() => {
    setUpdatedOfftakes(undefined)
  }, [boundary])

  const offtakeYears = useMemo(() => {
    if (!inventory.data?.item.years) return

    const firstBaselineYear = d3.min(inventory.data?.item.years) as number
    if (firstBaselineYear >= LATEST_OFFTAKE_YEAR) {
      return [LATEST_OFFTAKE_YEAR]
    }
    return d3.range(firstBaselineYear, LATEST_OFFTAKE_YEAR + 1)
  }, [inventory.data])

  if (!inventoryId) {
    return null
  }

  const boundaryCount = inventory.data?.item.boundaries.length

  const handleChangeOfftakeRows = (rows: any) => {
    setUpdatedOfftakes(rows)
  }

  const handleClickSave = () => {
    if (!boundary || !updatedOfftakes) return

    setBoundaryOfftakes.mutateAsync({
      inventoryId,
      boundaryId: boundary?.boundaryId,
      cropOfftakes: updatedOfftakes,
    })
  }

  return (
    <Stack direction="row" flex={1}>
      <Stack sx={{ height: '100%', position: 'relative' }}>
        <Typography mt={3} ml={3} mb={1.5} variant="h6">
          {t('title')}
        </Typography>

        <BoundariesListItem boundary={boundary} />

        {!!boundary && !!offtakeYears && (
          <Box minWidth={420} ml={3} mr={3} mt={0.5} flex={1}>
            <HistoricalOfftakeTable
              years={offtakeYears}
              boundary={boundary}
              onChange={handleChangeOfftakeRows}
            />
          </Box>
        )}

        <Paper>
          <Divider />
          <Stack p={2} direction="row" justifyContent="space-between">
            <ItemLocator
              index={selectedBoundaryIndex}
              count={boundaryCount ?? 0}
              onChange={setSelectedBoundaryIndex}
            />

            <LoadingButton
              variant="contained"
              disabled={!boundaryCount || !updatedOfftakes}
              onClick={handleClickSave}
              loading={setBoundaryOfftakes.isLoading}
            >
              {t('saveOfftakeData')}
            </LoadingButton>
          </Stack>
        </Paper>
      </Stack>

      <Box flex={1} sx={{ height: '100%', position: 'relative' }}>
        <MapGL
          style={{ height: '100%', width: '100%', position: 'absolute' }}
          focusFeatures={
            boundaryFeature.data
              ? (featureCollection([
                  boundaryFeature.data as Feature<Geometry>,
                ]) as FeatureCollection)
              : undefined
          }
        >
          <InventoryMapLayers />

          {!!boundaryFeature.data && <BoundaryAreaLayer feature={boundaryFeature.data} />}

          <NavigationControl showZoom position="bottom-right" />
          <Box style={{ position: 'absolute', bottom: 35, left: 8 }}>
            <LayerSwitcher />
          </Box>
        </MapGL>
      </Box>
    </Stack>
  )
}
